if (!customElements.get('creator-hub-v2-media')) {
  customElements.define('creator-hub-v2-media', class extends HTMLElement {
    connectedCallback() {
      this.video = this.querySelector('video');
      if (!this.video) return;

      this.events = new AbortController();
      const options = { signal: this.events.signal };
      this.mobile = matchMedia('(max-width: 640px)');
      this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
      this.inView = !('IntersectionObserver' in window);
      this.activeSource = '';
      this.userPaused = false;
      this.ignoredPauses = 0;
      this.toggle = this.querySelector('.chv2-media__toggle');
      this.video.muted = true;

      this.sync = () => this.updatePlayback();
      this.mobile.addEventListener('change', this.sync, options);
      this.reducedMotion.addEventListener('change', () => {
        if (this.reducedMotion.matches) this.pauseInternally();
        this.updatePlayback();
      }, options);
      document.addEventListener('visibilitychange', this.sync, options);
      this.video.addEventListener('play', () => {
        this.userPaused = false;
        if (!this.eligible()) this.pauseInternally();
        this.updateToggle();
      }, options);
      this.video.addEventListener('pause', () => {
        if (this.ignoredPauses) this.ignoredPauses--;
        else if (this.eligible()) this.userPaused = true;
        this.updateToggle();
      }, options);
      this.video.addEventListener('error', () => {
        this.wantAutoplay = false;
        this.failedSource = this.activeSource;
        this.video.hidden = true;
        if (this.toggle) this.toggle.hidden = true;
      }, options);
      this.toggle?.addEventListener('click', () => {
        if (this.video.paused) {
          this.userPaused = false;
          this.play();
        } else {
          this.userPaused = true;
          this.wantAutoplay = false;
          this.pauseInternally();
        }
      }, options);

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(entries => {
          this.inView = entries[0].isIntersecting;
          if (!this.inView) this.userPaused = false;
          this.updatePlayback();
        });
        this.observer.observe(this);
      }
      this.updatePlayback();
    }

    eligible() {
      return this.isConnected && this.inView && !document.hidden
        && !(this.mobile.matches && this.hasAttribute('data-desktop-only'));
    }

    updatePlayback() {
      this.wantAutoplay = false;
      if (!this.eligible()) {
        this.pauseInternally();
        // Never display or load the desktop gallery video on mobile.
        if (this.mobile.matches && this.hasAttribute('data-desktop-only')) {
          this.video.hidden = true;
          if (this.toggle) this.toggle.hidden = true;
        }
        return;
      }

      const device = this.mobile.matches ? 'mobile' : 'desktop';
      let sources;
      try {
        sources = JSON.parse(this.dataset[device + 'Sources'] || 'null') || [];
      } catch {
        sources = [];
      }
      const key = sources.map(source => source.url).join('|');
      if (key !== this.activeSource) {
        this.pauseInternally();
        this.video.replaceChildren(...sources.map(source => {
          const element = document.createElement('source');
          element.src = source.url;
          element.type = source.mime_type;
          return element;
        }));
        this.video.poster = this.dataset[device + 'Poster'] || '';
        this.activeSource = key;
        this.userPaused = false;
        this.video.load();
      }
      const available = !!key && key !== this.failedSource;
      this.video.hidden = !available;
      if (this.toggle) this.toggle.hidden = !available;
      this.wantAutoplay = available && !this.userPaused && !this.reducedMotion.matches;
      if (this.wantAutoplay && this.video.paused) this.play();
      if (!available) this.pauseInternally();
      this.updateToggle();
    }

    play() {
      const source = this.activeSource;
      if (this.pendingPlay === source) return;
      this.pendingPlay = source;
      this.video.play().then(() => {
        if (this.activeSource === source && (!this.eligible() || this.userPaused)) this.pauseInternally();
      }).catch(() => {
        // Native controls / the gallery toggle remain available if autoplay is blocked.
      }).finally(() => {
        if (this.pendingPlay === source) this.pendingPlay = '';
        this.updateToggle();
      });
    }

    pauseInternally() {
      if (!this.video.paused) {
        this.ignoredPauses++;
        this.video.pause();
      }
    }

    updateToggle() {
      if (!this.toggle) return;
      const paused = this.video.paused;
      this.toggle.setAttribute('aria-label', paused ? 'Play video' : 'Pause video');
      this.toggle.querySelector('[data-play-icon]').hidden = !paused;
      this.toggle.querySelector('[data-pause-icon]').hidden = paused;
    }

    disconnectedCallback() {
      this.wantAutoplay = false;
      this.observer?.disconnect();
      this.events?.abort();
      this.video?.pause();
    }
  });
}
